import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useContracts } from './useContracts';
import { SelectOption } from '@/components/ui/Select';

interface UseContractOptionsProps {
  includeInactive?: boolean;
  includeAll?: boolean;
  limit?: number;
  companyId?: string;
}

export function useContractOptions({
  includeInactive = false,
  includeAll = false,
  limit = 100,
  companyId,
}: UseContractOptionsProps = {}) {
  const { t } = useTranslation();
  const { contracts, activeContracts, isLoading } = useContracts({ limit });

  const contractOptions: SelectOption[] = useMemo(() => {
    let contractsToUse = includeInactive ? contracts : activeContracts;

    // Filter by company if companyId is provided and not 'all'
    if (companyId && companyId !== 'all') {
      contractsToUse = contractsToUse.filter((contract) => contract.company.id === companyId);
    }

    const options = contractsToUse.map((contract) => ({
      value: contract.id,
      content: contract.number,
    }));

    if (includeAll) {
      return [{ value: 'all', content: t('common.all') }, ...options];
    }

    return options;
  }, [contracts, activeContracts, includeInactive, includeAll, companyId, t]);

  return {
    contractOptions,
    isLoading,
  };
}
